import {
  AddCircleOutline as AddIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  Block as IgnoreIcon,
  SyncAlt as MergeIcon
} from '@mui/icons-material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { doc, getDoc, onSnapshot, serverTimestamp, updateDoc, writeBatch } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AuthGuard from '@/components/auth-guard';
import { Page } from '@/components/page';
import { useFirebase } from '@/firebase';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { useSubscription } from '@/hooks/useSubscription';

export const Route = createFileRoute('/import/review/$sessionId')({
  component: ImportReviewPage,
});

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`import-tabpanel-${index}`}
      aria-labelledby={`import-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function ImportReviewContent() {
  const { sessionId } = Route.useParams();
  const { novel, getSummaries, getItemRef } = useNovelRepository();
  const { firestore, backend } = useFirebase();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const getEntityTitle = (type: string, category?: string): string => {
    if (type === 'codex') {
      if (category) {
        switch (category) {
          case 'magicSystem':
            return t('entity_list.codex.categories.magicSystem');
          case 'races':
            return t('entity_list.codex.categories.races');
          case 'religions':
            return t('entity_list.codex.categories.religions');
          case 'technology':
            return t('entity_list.codex.categories.technology');
          case 'planets':
            return t('entity_list.codex.categories.planets');
          case 'caseFiles':
            return t('entity_list.codex.categories.caseFiles');
          case 'creatures':
            return t('entity_list.codex.categories.creatures');
          case 'rules':
            return t('entity_list.codex.categories.rules');
          default:
            return category;
        }
      }
      return t('entity_list.codex.title');
    }
    switch (type) {
      case 'characters':
        return t('entity_list.characters.title');
      case 'locations':
        return t('entity_list.locations.title');
      case 'organizations':
        return t('entity_list.organizations.title');
      default:
        return type;
    }
  };

  const [session, setSession] = useState<any>(null);
  const [partData, setPartData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [loadingPart, setLoadingPart] = useState(false);
  const [isCommitting, setIsCommitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [isJoining, setIsJoining] = useState(false);

  // Existing entities to merge with
  const existingCharacters = getSummaries('characters');
  const existingLocations = getSummaries('locations');
  const existingOrganizations = getSummaries('organizations');

  useEffect(() => {
    if (!novel.id || !firestore || !sessionId) return;

    const sessionRef = doc(firestore, 'users', novel.ownerId, 'novels', novel.id, 'import_sessions', sessionId);

    const unsubscribe = onSnapshot(
      sessionRef,
      (snapshot) => {
        setLoading(false);
        if (snapshot.exists()) {
          setSession(snapshot.data());
        } else {
          setErrorMsg('Import session not found.');
        }
      },
      (err) => {
        console.error('Error listening to session:', err);
        setErrorMsg('Failed to listen to session changes.');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [novel, firestore, sessionId]);

  useEffect(() => {
    if (!novel.id || !firestore || !sessionId || !session?.currentPartId) {
      setPartData(null);
      setLoadingPart(false);
      return;
    }

    setLoadingPart(true);
    const partRef = doc(firestore, 'users', novel.ownerId, 'novels', novel.id, 'import_sessions', sessionId, 'parts', session.currentPartId);

    const unsubscribe = onSnapshot(
      partRef,
      (snapshot) => {
        setLoadingPart(false);
        if (snapshot.exists()) {
          setPartData(snapshot.data());
        } else {
          console.warn('Part document not found:', session.currentPartId);
        }
      },
      (err) => {
        console.error('Error listening to part document:', err);
        setLoadingPart(false);
      }
    );

    return () => unsubscribe();
  }, [novel, firestore, sessionId, session?.currentPartId]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [tabValue]);

  const updatePartDoc = async (updates: Partial<any>) => {
    if (!novel.id || !firestore || !sessionId || !session?.currentPartId) return;
    const partRef = doc(firestore, 'users', novel.ownerId, 'novels', novel.id, 'import_sessions', sessionId, 'parts', session.currentPartId);
    try {
      await updateDoc(partRef, updates);
    } catch (err) {
      console.error('Failed to update part document:', err);
    }
  };

  const handleEntityActionChange = (entityIdx: number, action: string) => {
    const updatedEntities = [...(partData?.extractedEntities || [])];
    updatedEntities[entityIdx] = {
      ...updatedEntities[entityIdx],
      userAction: action,
    };
    updatePartDoc({ extractedEntities: updatedEntities });
  };

  const handleEntitySelectionChange = (entityIdx: number, selectedId: string | null) => {
    const updatedEntities = [...(partData?.extractedEntities || [])];
    updatedEntities[entityIdx] = {
      ...updatedEntities[entityIdx],
      reconciliation: {
        ...updatedEntities[entityIdx].reconciliation,
        selectedEntityId: selectedId,
      },
    };
    updatePartDoc({ extractedEntities: updatedEntities });
  };

  const handleMinorToggle = (entityIdx: number, isMinor: boolean) => {
    const updatedEntities = [...(partData?.extractedEntities || [])];
    updatedEntities[entityIdx] = {
      ...updatedEntities[entityIdx],
      reconciliation: {
        ...updatedEntities[entityIdx].reconciliation,
        isMinor,
      },
    };
    updatePartDoc({ extractedEntities: updatedEntities });
  };

  const handleRelationshipApproveToggle = (idx: number, isApproved: boolean) => {
    const updated = [...(partData?.extractedRelationships || [])];
    updated[idx] = { ...updated[idx], isApproved };
    updatePartDoc({ extractedRelationships: updated });
  };

  const handleEventApproveToggle = (idx: number, isApproved: boolean) => {
    const updated = [...(partData?.extractedEvents || [])];
    updated[idx] = { ...updated[idx], isApproved };
    updatePartDoc({ extractedEvents: updated });
  };

  const handleEventChangeApproveToggle = (idx: number, isApproved: boolean) => {
    const updated = [...(partData?.extractedEventChanges || [])];
    updated[idx] = { ...updated[idx], isApproved };
    updatePartDoc({ extractedEventChanges: updated });
  };

  const handleCommit = async () => {
    if (!novel.id) return;
    setIsCommitting(true);
    setErrorMsg('');

    try {
      const res = await backend.ai.commitNovelKnowledgePart({
        novelId: novel.id,
        sessionId,
      });
      if (res?.hasMoreParts) {
        navigate({ to: '/import/processing/$sessionId', params: { sessionId } });
      } else {
        navigate({ to: '/' });
      }
    } catch (err: unknown) {
      console.error('Failed to commit import:', err);
      const msg = err instanceof Error ? err.message : 'Failed to apply import changes to the novel.';
      setErrorMsg(msg);
      setIsCommitting(false);
    }
  };

  const handleJoinSceneWithPrevious = async (chapterIdx: number, sceneIdx: number) => {
    if (!novel.id || !firestore || !session || isJoining) return;

    const chapter = session.persistedStructure?.chapters?.[chapterIdx];
    if (!chapter || sceneIdx <= 0) return;

    const sceneToMerge = chapter.scenes?.[sceneIdx];
    const targetScene = chapter.scenes?.[sceneIdx - 1];
    if (!sceneToMerge || !targetScene) return;

    setIsJoining(true);
    setErrorMsg('');

    try {
      const targetPartRef = doc(firestore, 'users', novel.ownerId, 'novels', novel.id, 'import_sessions', sessionId, 'parts', targetScene.id);
      const mergePartRef = doc(firestore, 'users', novel.ownerId, 'novels', novel.id, 'import_sessions', sessionId, 'parts', sceneToMerge.id);
      const targetPartSnap = await getDoc(targetPartRef);
      const mergePartSnap = await getDoc(mergePartRef);

      if (!targetPartSnap.exists() || !mergePartSnap.exists()) {
        throw new Error('One or both scene part documents do not exist.');
      }

      const targetPartData = targetPartSnap.data();
      const mergePartData = mergePartSnap.data();

      if (!targetPartData || !mergePartData) {
        throw new Error('Scene part data is empty.');
      }

      const targetProseVal = targetPartData.prose;
      const mergeProseVal = mergePartData.prose;
      const targetWordCountVal = targetPartData.wordCount;
      const mergeWordCountVal = mergePartData.wordCount;

      const targetProse = typeof targetProseVal === 'string' ? targetProseVal : '';
      const mergeProse = typeof mergeProseVal === 'string' ? mergeProseVal : '';
      const combinedProse = `${targetProse}\n\n${mergeProse}`;

      const targetWordCount = typeof targetWordCountVal === 'number' ? targetWordCountVal : 0;
      const mergeWordCount = typeof mergeWordCountVal === 'number' ? mergeWordCountVal : 0;
      const combinedWordCount = targetWordCount + mergeWordCount;

      interface StructureScene {
        id: string;
        name: string;
        order: number;
      }
      interface StructureChapter {
        id: string;
        title: string;
        order: number;
        scenes: StructureScene[];
      }

      const updatedChapters: StructureChapter[] = JSON.parse(JSON.stringify(session.persistedStructure.chapters));
      const chapterScenes = updatedChapters[chapterIdx].scenes;
      chapterScenes.splice(sceneIdx, 1);
      chapterScenes.forEach((s: StructureScene, idx: number) => {
        s.order = idx + 1;
      });

      const newPersistedStructure = {
        ...session.persistedStructure,
        chapters: updatedChapters,
      };

      interface PartItem {
        id: string;
        name: string;
        type: string;
        chunkIndex?: number;
      }

      const originalPartsList: PartItem[] = session.partsList || [];
      const newPartsList = originalPartsList.filter((p: PartItem) => p.id !== sceneToMerge.id);
      const newTotalParts = newPartsList.length;

      let newCurrentPartId = '';
      if (session && typeof session.currentPartId === 'string') {
        newCurrentPartId = session.currentPartId;
      }
      if (newCurrentPartId === sceneToMerge.id) {
        newCurrentPartId = targetScene.id;
      }

      const batch = writeBatch(firestore);

      // Update target part prose and word count
      batch.update(targetPartRef, {
        prose: combinedProse,
        wordCount: combinedWordCount,
      });

      // Delete merged part document in subcollection
      batch.delete(mergePartRef);

      // Update parent session document
      const sessionRef = doc(firestore, 'users', novel.ownerId, 'novels', novel.id, 'import_sessions', sessionId);
      batch.update(sessionRef, {
        persistedStructure: newPersistedStructure,
        partsList: newPartsList,
        totalParts: newTotalParts,
        currentPartId: newCurrentPartId,
        updatedAt: serverTimestamp(),
      });

      await batch.commit();
    } catch (err: unknown) {
      console.error('Failed to merge scenes:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to join scenes.';
      setErrorMsg(errorMessage);
    } finally {
      setIsJoining(false);
    }
  };

  if (loading || (session?.currentPartId && loadingPart && !partData)) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 12, gap: 2 }}>
        <CircularProgress color="primary" />
        <Typography variant="body1" color="text.secondary">{t('import.loading_review')}</Typography>
      </Box>
    );
  }

  if (errorMsg && !session) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h3" color="error" gutterBottom>{t('import.failed_title')}</Typography>
        <Typography variant="body1">{errorMsg}</Typography>
      </Box>
    );
  }

  const getExistingEntities = (type: string) => {
    if (type === 'characters') return existingCharacters;
    if (type === 'locations') return existingLocations;
    if (type === 'organizations') return existingOrganizations;
    return [];
  };

  const isEntityIgnored = (id: string): boolean => {
    const ent = partData?.extractedEntities?.find((e: any) => e.id === id);
    return ent?.userAction === 'ignore';
  };

  const getResolvedEntityName = (id: string, defaultName: string, entityType: string): string => {
    const ent = partData?.extractedEntities?.find((e: { id: string; userAction: string; name: string; reconciliation?: { selectedEntityId?: string | null; matchedEntityId?: string | null; }; entityType: string; }) => e.id === id);
    if (ent) {
      if (ent.userAction === 'ignore') {
        return `${ent.name} (${t('import.action_ignored')})`;
      }
      if (ent.userAction === 'merge') {
        const targetId = ent.reconciliation?.selectedEntityId || ent.reconciliation?.matchedEntityId;
        if (targetId) {
          const existingList = getExistingEntities(ent.entityType);
          const matched = existingList.find((x) => x.id === targetId);
          if (matched) {
            const mName = 'name' in matched ? matched.name : 'title' in matched ? matched.title : '';
            return `${mName} (${t('import.action_merged')})`;
          }
        }
      }
      return ent.name;
    }

    const existingList = getExistingEntities(entityType);
    const matched = existingList.find((x) => x.id === id);
    if (matched) {
      return 'name' in matched ? matched.name : 'title' in matched ? matched.title : defaultName;
    }

    return defaultName;
  };

  const normalizeFieldKey = (field: string): string => {
    let key = field.trim();
    if (key.startsWith("details.")) {
      key = key.substring(8);
    }
    if (key.includes(".")) {
      key = key
        .split(".")
        .map((part, index) => {
          if (index === 0) return part;
          return part.charAt(0).toUpperCase() + part.slice(1);
        })
        .join("");
    }
    return key;
  };

  const getResolvedValueName = (value: unknown): string => {
    if (Array.isArray(value)) {
      return `[${value.map((val: unknown) => getResolvedValueName(val)).join(', ')}]`;
    }
    if (typeof value !== 'string') {
      return JSON.stringify(value);
    }
    const ent = partData?.extractedEntities?.find((e: {
      id: string;
      name: string;
      userAction: string;
      reconciliation?: {
        selectedEntityId?: string | null;
        matchedEntityId?: string | null;
      };
      entityType: string;
    }) => e.id === value);

    if (ent) {
      if (ent.userAction === 'ignore') {
        return `${ent.name} (${t('import.action_ignored')})`;
      }
      if (ent.userAction === 'merge') {
        const targetId = ent.reconciliation?.selectedEntityId || ent.reconciliation?.matchedEntityId;
        if (targetId) {
          const existingList = getExistingEntities(ent.entityType);
          const matched = existingList.find((x) => x.id === targetId);
          if (matched) {
            const mName = 'name' in matched ? matched.name : 'title' in matched ? matched.title : '';
            return `${mName} (${t('import.action_merged')})`;
          }
        }
      }
      return ent.name;
    }

    const allExisting = [...existingCharacters, ...existingLocations, ...existingOrganizations];
    const matchedExisting = allExisting.find((x) => x.id === value);
    if (matchedExisting) {
      const mName = 'name' in matchedExisting ? matchedExisting.name : 'title' in matchedExisting ? matchedExisting.title : '';
      return mName;
    }

    return value;
  };

  return (
    <Page
      title={t('import.review_title')}
      description={t('import.review_desc')}
      layoutMode="scrolling"
    >

      {errorMsg && (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 2 }}>
          <Typography variant="body2">{errorMsg}</Typography>
        </Box>
      )}

      <Box sx={{ width: '100%', mt: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={(_e, val) => setTabValue(val)}
            textColor="inherit"
            indicatorColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTabs-indicator': {
                bgcolor: '#C89619',
              },
            }}
          >
            <Tab label={t('import.tab_structure')} sx={{ textTransform: 'none', fontWeight: 600 }} />
            <Tab
              label={`${t('import.tab_entities')} (${partData?.extractedEntities?.length || 0})`}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
            <Tab
              label={`${t('import.tab_relationships')} (${partData?.extractedRelationships?.length || 0})`}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
            <Tab
              label={`${t('import.tab_events')} (${partData?.extractedEvents?.length || 0})`}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
          </Tabs>
        </Box>

        {/* TAB 1: STRUCTURE */}
        <CustomTabPanel value={tabValue} index={0}>
          <Card sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h3" sx={{ mb: 3, fontWeight: 600 }}>{t('import.structure_title')}</Typography>
              {session.persistedStructure?.chapters?.length > 0 ? (
                session.persistedStructure.chapters.map((chapter: any, cIdx: number) => (
                  <Accordion
                    key={chapter.id}
                    defaultExpanded
                    elevation={0}
                    disableGutters
                    sx={{
                      mb: 2,
                      borderRadius: 2,
                      bgcolor: 'background.default',
                      border: '1px solid',
                      borderColor: 'divider',
                      backgroundImage: 'none',
                      '&:before': { display: 'none' }
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        bgcolor: 'background.default',
                        borderRadius: 2,
                      }}
                    >
                      <Typography variant="h4" sx={{ fontWeight: 600 }}>
                        {t('import.chapter_order_label', { order: chapter.order })}: {chapter.title}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ bgcolor: 'action.hover', borderBottomLeftRadius: 8, borderBottomRightRadius: 8 }}>
                      {chapter.scenes?.length > 0 ? (
                        chapter.scenes.map((scene: any, sIdx: number) => (
                          <Box key={scene.id} sx={{ py: 1.5, px: 2, mb: 1, bgcolor: 'background.paper', borderRadius: 1.5, border: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {t('import.scene_order_label', { order: scene.order })}: {scene.name}
                            </Typography>
                            {sIdx > 0 && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                startIcon={isJoining ? <CircularProgress size={16} /> : <MergeIcon />}
                                onClick={() => handleJoinSceneWithPrevious(cIdx, sIdx)}
                                disabled={isJoining}
                                sx={{ textTransform: 'none', borderRadius: 2 }}
                              >
                                {t('import.join_with_previous')}
                              </Button>
                            )}
                          </Box>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">{t('import.no_scenes_in_chapter')}</Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))
              ) : (
                <Typography variant="body1" color="text.secondary">
                  {t('import.no_structure_desc')}
                </Typography>
              )}
            </CardContent>
          </Card>
        </CustomTabPanel>

        {/* TAB 2: ENTITIES */}
        <CustomTabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            {partData?.extractedEntities?.length > 0 ? (
              partData.extractedEntities.map((entity: any, idx: number) => {
                const existingList = getExistingEntities(entity.entityType);
                const isMatch = entity.reconciliation?.status === 'match';
                const isAmbiguous = entity.reconciliation?.status === 'ambiguous';

                return (
                  <Grid key={entity.id} size={12}>
                    <Card sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                      <CardContent sx={{ p: 3 }}>
                        <Grid container spacing={3} alignItems="flex-start">
                          <Grid size={{ xs: 12, md: 7 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                              <Typography variant="h4" sx={{ fontWeight: 600 }}>{entity.name}</Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, borderRadius: 1, bgcolor: 'action.selected' }}>
                                <Typography variant="caption" sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}>
                                  {getEntityTitle(entity.entityType, entity.category)}
                                </Typography>
                              </Box>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              {entity.shortDescription}
                            </Typography>
                            <Box sx={{ bgcolor: 'action.hover', p: 1.5, borderRadius: 2, borderLeft: '3px solid', borderColor: 'primary.light' }}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                                "...{entity.contextSnippet}..."
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid size={{ xs: 12, md: 5 }}>
                            <Box sx={{ mb: 2 }}>
                              <Chip
                                label={t('import.reconciliation_suggestion', { status: entity.reconciliation.status })}
                                color={isMatch ? 'success' : isAmbiguous ? 'warning' : 'primary'}
                                size="small"
                                variant="outlined"
                                sx={{ fontWeight: 600, textTransform: 'capitalize' }}
                              />
                            </Box>

                            <FormControl fullWidth variant="outlined" size="small" sx={{ mb: 2 }}>
                              <InputLabel id={`action-label-${entity.id}`}>{t('import.action_label')}</InputLabel>
                              <Select
                                labelId={`action-label-${entity.id}`}
                                value={entity.userAction}
                                label={t('import.action_label')}
                                onChange={(e) => handleEntityActionChange(idx, e.target.value)}
                                sx={{ borderRadius: 2 }}
                              >
                                <MenuItem value="approve">
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AddIcon fontSize="small" color="primary" />
                                    <Typography variant="body2">{t('import.action_new')}</Typography>
                                  </Box>
                                </MenuItem>
                                <MenuItem value="merge">
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <MergeIcon fontSize="small" color="success" />
                                    <Typography variant="body2">{t('import.action_merge')}</Typography>
                                  </Box>
                                </MenuItem>
                                <MenuItem value="ignore">
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <IgnoreIcon fontSize="small" color="error" />
                                    <Typography variant="body2">{t('import.action_ignore')}</Typography>
                                  </Box>
                                </MenuItem>
                              </Select>
                            </FormControl>

                            {entity.userAction === 'merge' && (
                              <FormControl fullWidth variant="outlined" size="small" sx={{ mb: 2 }}>
                                <InputLabel id={`merge-label-${entity.id}`}>{t('import.merge_target_label')}</InputLabel>
                                <Select
                                  labelId={`merge-label-${entity.id}`}
                                  value={entity.reconciliation.selectedEntityId || ''}
                                  label={t('import.merge_target_label')}
                                  onChange={(e) => handleEntitySelectionChange(idx, e.target.value)}
                                  sx={{ borderRadius: 2 }}
                                >
                                  {existingList.map((ext: any) => (
                                    <MenuItem key={ext.id} value={ext.id}>
                                      {ext.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )}

                            {entity.entityType === 'characters' && (
                              <Box sx={{ mt: 2, display: 'flex', alignItems: 'flex-start' }}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={entity.reconciliation.isMinor || false}
                                      onChange={(e) => handleMinorToggle(idx, e.target.checked)}
                                      color="primary"
                                      sx={{ pt: 0 }}
                                    />
                                  }
                                  label={
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{t('import.minor_character_label')}</Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                                        {t('import.minor_character_desc')}
                                      </Typography>
                                    </Box>
                                  }
                                  sx={{ alignItems: 'flex-start', margin: 0 }}
                                />
                              </Box>
                            )}
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })
            ) : (
              <Grid size={12}>
                <Typography variant="body1" color="text.secondary">{t('import.no_entities_extracted')}</Typography>
              </Grid>
            )}
          </Grid>
        </CustomTabPanel>

        {/* TAB 3: RELATIONSHIPS */}
        <CustomTabPanel value={tabValue} index={2}>
          <Card sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h3" sx={{ mb: 3, fontWeight: 600 }}>{t('import.relationships_title')}</Typography>
              {partData?.extractedRelationships?.length > 0 ? (
                partData.extractedRelationships.map((rel: any, idx: number) => {
                  const ignoredA = isEntityIgnored(rel.entityA.id);
                  const ignoredB = isEntityIgnored(rel.entityB.id);
                  const isDisabled = ignoredA || ignoredB;

                  const nameA = getResolvedEntityName(rel.entityA.id, rel.entityA.name, rel.entityA.type);
                  const nameB = getResolvedEntityName(rel.entityB.id, rel.entityB.name, rel.entityB.type);

                  const isApproved = isDisabled ? false : rel.isApproved;

                  return (
                    <Box key={rel.id} sx={{ mb: 2, opacity: isDisabled ? 0.6 : 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>{nameA}</Typography>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {rel.reverseDescription ? (
                                <>
                                  <Chip
                                    label={rel.forwardDescription}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ fontWeight: 600 }}
                                  />
                                  <MergeIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
                                  <Chip
                                    label={rel.reverseDescription}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ fontWeight: 600 }}
                                  />
                                </>
                              ) : (
                                <>
                                  <Chip
                                    label={rel.forwardDescription}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ fontWeight: 600 }}
                                  />
                                  <ArrowForwardIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
                                </>
                              )}
                            </Box>

                            <Typography variant="body1" sx={{ fontWeight: 600 }}>{nameB}</Typography>

                            {rel.reverseDescription && (
                              <Chip
                                label={t('import.bidirectional')}
                                size="small"
                                color="success"
                                variant="filled"
                                sx={{ fontWeight: 'bold', fontSize: '0.75rem', height: 20 }}
                              />
                            )}
                          </Box>

                          {isDisabled && (
                            <Typography variant="caption" sx={{ color: 'error.main', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              ⚠️ {t('import.rel_disabled_warning')}
                            </Typography>
                          )}
                        </Box>
                        <Tooltip title={isDisabled ? t('import.rel_disabled_warning') : ""}>
                          <span>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={isApproved}
                                  onChange={(e) => handleRelationshipApproveToggle(idx, e.target.checked)}
                                  color="primary"
                                  disabled={isDisabled}
                                />
                              }
                              label={isApproved ? t('import.approved_label') : t('import.ignored_label')}
                            />
                          </span>
                        </Tooltip>
                      </Box>
                    </Box>
                  );
                })
              ) : (
                <Typography variant="body1" color="text.secondary">{t('import.no_relationships_extracted')}</Typography>
              )}
            </CardContent>
          </Card>
        </CustomTabPanel>

        {/* TAB 4: EVENTS & CHANGES */}
        <CustomTabPanel value={tabValue} index={3}>
          <Card sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h3" sx={{ mb: 3, fontWeight: 600 }}>{t('import.events_title')}</Typography>
              {partData?.extractedEvents?.length > 0 ? (
                partData.extractedEvents.map((evt: any, idx: number) => {
                  const associatedChanges = (partData?.extractedEventChanges || []).filter(
                    (change: any) => change.eventId === evt.id
                  );

                  return (
                    <Box
                      key={evt.id}
                      sx={{
                        p: 3,
                        mb: 3,
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.default',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>{evt.title}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            {evt.description}
                          </Typography>
                          {evt.whyItMatters && (
                            <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic', color: 'text.secondary' }}>
                              {t('import.why_it_matters_label', { why: evt.whyItMatters })}
                            </Typography>
                          )}
                        </Box>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={evt.isApproved}
                              onChange={(e) => handleEventApproveToggle(idx, e.target.checked)}
                              color="primary"
                            />
                          }
                          label={evt.isApproved ? t('import.approved_label') : t('import.ignored_label')}
                          sx={{ m: 0 }}
                        />
                      </Box>

                      {associatedChanges.length > 0 && (
                        <Box sx={{ mt: 3, pl: { xs: 1, md: 3 }, borderLeft: '2px solid', borderColor: 'primary.main' }}>
                          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>
                            {t('import.associated_changes')}
                          </Typography>
                          <Grid container spacing={2}>
                            {associatedChanges.map((change: any) => {
                              const globalIdx = partData.extractedEventChanges.findIndex((c: any) => c.id === change.id);
                              const isIgnored = isEntityIgnored(change.entityId);
                              const resolvedName = getResolvedEntityName(change.entityId, change.entityName, change.entityType);
                              const isApproved = isIgnored ? false : change.isApproved;

                              return (
                                <Grid key={change.id} size={{ xs: 12, md: 6 }}>
                                  <Box
                                    sx={{
                                      p: 2,
                                      borderRadius: 2,
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      bgcolor: 'background.paper',
                                      opacity: isIgnored ? 0.6 : 1,
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {t('import.change_name', { name: resolvedName })}
                                      </Typography>
                                      <Tooltip title={isIgnored ? t('import.change_disabled_warning') : ""}>
                                        <span>
                                          <FormControlLabel
                                            control={
                                              <Switch
                                                checked={isApproved}
                                                onChange={(e) => {
                                                  if (globalIdx !== -1) {
                                                    handleEventChangeApproveToggle(globalIdx, e.target.checked);
                                                  }
                                                }}
                                                color="primary"
                                                size="small"
                                                disabled={isIgnored}
                                              />
                                            }
                                            label=""
                                            sx={{ m: 0 }}
                                          />
                                        </span>
                                      </Tooltip>
                                    </Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                      {change.description}
                                    </Typography>
                                    {isIgnored && (
                                      <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mb: 1.5 }}>
                                        ⚠️ {t('import.change_disabled_warning')}
                                      </Typography>
                                    )}
                                    <Box sx={{ bgcolor: 'action.hover', p: 1, borderRadius: 1 }}>
                                      {change.changes?.map((ch: any, cIdx: number) => {
                                        const normField = normalizeFieldKey(ch.field);
                                        return (
                                          <Typography key={cIdx} variant="caption" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                            {normField} → {getResolvedValueName(ch.newValue)}
                                          </Typography>
                                        );
                                      })}
                                    </Box>
                                  </Box>
                                </Grid>
                              );
                            })}
                          </Grid>
                        </Box>
                      )}
                    </Box>
                  );
                })
              ) : (
                <Typography variant="body1" color="text.secondary">{t('import.no_events_extracted')}</Typography>
              )}
            </CardContent>
          </Card>
        </CustomTabPanel>
      </Box>

      {/* COMMIT BAR */}
      <Divider sx={{ my: 4 }} />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pb: 6 }}>
        <Button
          variant="outlined"
          onClick={() => navigate({ to: '/import' })}
          disabled={isCommitting}
          sx={{ borderRadius: 2.5, textTransform: 'none', px: 4, py: 1.5 }}
        >
          {t('editor.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={tabValue < 3 ? () => setTabValue(tabValue + 1) : handleCommit}
          disabled={isCommitting}
          startIcon={
            tabValue < 3 ? (
              <ArrowForwardIcon />
            ) : isCommitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <CheckIcon />
            )
          }
          sx={{
            borderRadius: 2.5,
            px: 6,
            py: 1.5,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '1rem',
          }}
        >
          {tabValue < 3 ? t('common.continue') : (isCommitting ? t('import.btn_committing') : t('import.commit_onboarding_btn'))}
        </Button>
      </Box>
    </Page>
  );
}

export default function ImportReviewPage() {
  const { hasFeature, loading } = useSubscription();
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!hasFeature('ai_assistance_allowed')) {
    return (
      <Card sx={{ maxWidth: 500, mx: 'auto', mt: 8, textAlign: 'center', p: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <CardContent>
          <Typography variant="h2" gutterBottom color="error.main" sx={{ fontWeight: 600 }}>
            {t('import.access_denied_title')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, mt: 1 }}>
            {t('import.access_denied_desc')}
          </Typography>
          <Button variant="contained" color="primary" onClick={() => navigate({ to: '/' })} sx={{ borderRadius: 2 }}>
            {t('import.return_to_dashboard')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <AuthGuard>
      <ImportReviewContent />
    </AuthGuard>
  );
}
